import React from 'react'
import axios from 'axios'
import {
    MdErrorOutline,
    MdLink,
    MdRefresh,
    MdSearch,
} from 'react-icons/md'
import './find-family-profile-search-box.css'
import { baseUrl } from '../../../../assets/Animations'
import { getRequestHeaders } from '../../../../api'
import { formatParentName } from '../contactInfoCopyUtils'

const normalizeSchool = (school) => String(school || '').trim().toLowerCase()

const FindFamilyProfileSearchBox = ({
    modalInfo,
    setModalInfo,
    getFamilyInfo,
    missingFamilyId,
    loadError,
}) => {
    const [query, setQuery] = React.useState('')
    const [familySuggestions, setFamilySuggestions] = React.useState([])
    const [searching, setSearching] = React.useState(false)
    const [connectingFamilyId, setConnectingFamilyId] = React.useState('')
    const [error, setError] = React.useState('')
    const requestSequence = React.useRef(0)

    React.useEffect(() => {
        const trimmedQuery = query.trim()
        const currentSequence = requestSequence.current + 1
        requestSequence.current = currentSequence

        if (trimmedQuery.length < 2) {
            setFamilySuggestions([])
            setSearching(false)
            setError('')
            return undefined
        }

        setSearching(true)
        setError('')
        const timeout = setTimeout(() => {
            axios
                .get(
                    `${baseUrl}/search/${encodeURIComponent(trimmedQuery)}`,
                    getRequestHeaders()
                )
                .then((res) => {
                    if (requestSequence.current !== currentSequence) return
                    const suggestions = Array.isArray(res.data) ? res.data : []
                    const cardSchool = normalizeSchool(modalInfo?.whichSchool)
                    setFamilySuggestions([...suggestions].sort((left, right) => {
                        const leftMatches = normalizeSchool(left?.school) === cardSchool ? 0 : 1
                        const rightMatches = normalizeSchool(right?.school) === cardSchool ? 0 : 1
                        return leftMatches - rightMatches
                    }))
                })
                .catch((searchError) => {
                    if (requestSequence.current !== currentSequence) return
                    console.error('Unable to search for a replacement family:', searchError)
                    setFamilySuggestions([])
                    setError('Family search is unavailable right now. Please try again.')
                })
                .finally(() => {
                    if (requestSequence.current === currentSequence) {
                        setSearching(false)
                    }
                })
        }, 250)

        return () => clearTimeout(timeout)
    }, [modalInfo?.whichSchool, query])

    const selectTheFamily = async (family) => {
        if (!family?.familyId || connectingFamilyId) return

        setConnectingFamilyId(family.familyId)
        setError('')
        try {
            await axios.put(
                `${baseUrl}/student-onboarding/changeId/${modalInfo.todoId}`,
                { familyId: family.familyId },
                getRequestHeaders()
            )
            setModalInfo((previousCard) => ({
                ...previousCard,
                familyId: family.familyId,
            }))
        } catch (connectionError) {
            console.error('Unable to connect this card to the selected family:', connectionError)
            setError(
                connectionError?.response?.data?.message
                || 'This card could not be connected. Please try again.'
            )
            setConnectingFamilyId('')
        }
    }

    const hasSearchQuery = query.trim().length >= 2
    const hasMissingFamily = Boolean(missingFamilyId && missingFamilyId !== 'none')

    return (
        <div className={`find-family-profile-search-box__wrapper${hasMissingFamily ? ' find-family-profile-search-box__wrapper--missing' : ''}`}>
            <div className={`find-family-profile-search-box__status-icon${hasMissingFamily ? ' find-family-profile-search-box__status-icon--missing' : ''}`} aria-hidden='true'>
                {hasMissingFamily ? <MdErrorOutline size={25} /> : <MdLink size={23} />}
            </div>
            <div className='find-family-profile-search-box__title'>
                {hasMissingFamily ? 'Family record missing' : 'Connect this card to a family'}
            </div>
            <div className='find-family-profile-search-box__description'>
                {hasMissingFamily
                    ? 'The family previously connected to this card has been deleted. Search for the correct family below to reconnect the card without losing its workflow.'
                    : 'Search for an existing family profile to connect it to this onboarding card.'}
            </div>

            {loadError && (
                <div className='find-family-profile-search-box__load-error' role='alert'>
                    <span>{loadError}</span>
                    <button type='button' onClick={getFamilyInfo}>
                        <MdRefresh aria-hidden='true' /> Retry
                    </button>
                </div>
            )}

            <label
                className='find-family-profile-search-box__label'
                htmlFor={`family-reconnect-search-${modalInfo?.todoId || 'card'}`}
            >
                Find the correct family
            </label>
            <div className='find-family-profile-search-box__search-wrapper'>
                <MdSearch size={19} aria-hidden='true' />
                <input
                    id={`family-reconnect-search-${modalInfo?.todoId || 'card'}`}
                    className='find-family-profile-search-box__search'
                    placeholder='Search by family name'
                    type='search'
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    autoComplete='off'
                />
            </div>

            <div className='find-family-profile-search-box__results' aria-live='polite'>
                {searching && (
                    <div className='find-family-profile-search-box__message'>Searching…</div>
                )}
                {!searching && error && (
                    <div className='find-family-profile-search-box__message find-family-profile-search-box__message--error'>
                        {error}
                    </div>
                )}
                {!searching && !error && hasSearchQuery && familySuggestions.length === 0 && (
                    <div className='find-family-profile-search-box__message'>
                        No matching families found.
                    </div>
                )}
                {!searching && familySuggestions.map((family) => {
                    const isConnecting = connectingFamilyId === family.familyId
                    const studentNames = (Array.isArray(family.students) ? family.students : [])
                        .map((student) => student?.studentName)
                        .filter(Boolean)
                        .join(', ')

                    return (
                        <button
                            type='button'
                            className='find-family-profile-search-box__suggestion'
                            key={family.familyId}
                            disabled={Boolean(connectingFamilyId)}
                            onClick={() => selectTheFamily(family)}
                        >
                            <span className='find-family-profile-search-box__suggestion-main'>
                                <span className='find-family-profile-search-box__suggestion-name'>
                                    {formatParentName(family) || 'Unnamed family'}
                                </span>
                                <span className='find-family-profile-search-box__suggestion-details'>
                                    {[family.primaryEmail, studentNames]
                                        .filter(Boolean)
                                        .join(' • ')}
                                </span>
                            </span>
                            <span className='find-family-profile-search-box__suggestion-school'>
                                {isConnecting ? 'Connecting…' : family.school || 'No school'}
                            </span>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

export default FindFamilyProfileSearchBox
